import { NestedPersonnelClub } from "../types/personnel";

type AffiliationLike =
    | {
          name?: string | null;
          level?: string | null;
          orgAbbreviation?: string | null;
          abbreviation?: string | null;
          teamId?: number | null;
      }
    | null
    | undefined;

type PersonnelDisplaySource = {
    club?: NestedPersonnelClub | null;
    clubStatsId?: number | null;
    affiliation?: AffiliationLike;
    personnelType?: { type?: string | null } | null;
};

const isClubPersonnel = (personnel: PersonnelDisplaySource): boolean => {
    const type = personnel?.personnelType?.type;
    return type != null && type.toUpperCase() === "CLUB";
};

/** Prefer nested club when present, clubStatsId linked, or personnel is CLUB; else affiliation (VMS/UMPIRE). */
export const usesClubDisplay = (personnel: PersonnelDisplaySource): boolean => {
    if (personnel?.club != null) {
        return true;
    }
    if (personnel?.clubStatsId != null) {
        return true;
    }
    if (personnel?.club?.statsTeamId != null) {
        return true;
    }
    return isClubPersonnel(personnel);
};

export const getOrgAbbreviation = (personnel: PersonnelDisplaySource): string => {
    if (usesClubDisplay(personnel)) {
        return personnel.club?.orgAbbreviation ?? personnel.club?.abbreviation ?? "";
    }
    return personnel.affiliation?.orgAbbreviation ?? personnel.affiliation?.abbreviation ?? "";
};

export const getOrgOrClubName = (personnel: PersonnelDisplaySource): string => {
    if (usesClubDisplay(personnel)) {
        return personnel.club?.name ?? "";
    }
    return personnel.affiliation?.name ?? "";
};

export const getOrgOrClubLevel = (personnel: PersonnelDisplaySource): string => {
    if (usesClubDisplay(personnel)) {
        return personnel.club?.level ?? "";
    }
    return personnel.affiliation?.level ?? "";
};

/** Display label like "Yankees (Major)" / "Somerset (AAA)". */
export const buildAffiliateNameLevel = (personnel: PersonnelDisplaySource): string => {
    const name = getOrgOrClubName(personnel);
    if (!name) {
        return "";
    }
    const level = getOrgOrClubLevel(personnel);
    if (!level) {
        return name;
    }
    return `${name} (${level.trim()})`;
};

/** Header label: "NYY - Yankees". */
export const buildOrgHeaderLabel = (personnel: PersonnelDisplaySource): string => {
    const abbrev = getOrgAbbreviation(personnel);
    const name = getOrgOrClubName(personnel);
    if (abbrev && name) {
        return `${abbrev} - ${name}`;
    }
    return abbrev || name || "";
};

export const resolveClubStatsId = (personnel: PersonnelDisplaySource): number | null => {
    if (personnel?.clubStatsId != null) {
        return personnel.clubStatsId;
    }
    if (personnel?.club?.statsTeamId != null) {
        return personnel.club.statsTeamId;
    }
    return null;
};
